import React, { useEffect, useState } from "react";
import AdminNavBar from "../components/AdminNavBar";
import { Bell, RefreshCw, AlertTriangle, CheckCheck } from "lucide-react";
import { useAdminNotifications } from "../store/admin";
import { toDisplayString } from "../utils/toDisplayString";
import StaffPagination from "./components/staff/StaffPagination";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

/** Map a dead-letter record into the row shape rendered below. */
const mapDeadLetter = (raw) => ({
  id: raw.id,
  event: raw.eventType || raw.type || raw.event || raw.topic || "—",
  target: raw.targetType || raw.target || raw.queue || "—",
  attempts: raw.attempts ?? raw.retryCount ?? raw.attemptCount ?? "—",
  error: toDisplayString(raw.lastError || raw.error || raw.reason || raw.message, "—"),
  createdAt: raw.createdAt || raw.failedAt || raw.updatedAt,
});

const Notifications = () => {
  const {
    notifications,
    notificationsLoading,
    notificationsError,
    notificationsTotal,
    notificationsPages,
    fetchNotifications,
    notificationDetail,
    notificationDetailLoading,
    notificationDetailError,
    fetchNotification,
    markNotificationRead,
    markAllNotificationsRead,
    unreadCount,
    fetchUnreadNotificationCount,
    deadLetters,
    deadLettersLoading,
    deadLettersError,
    deadLettersTotal,
    notificationMutationLoading,
    fetchDeadLetters,
    replayDeadLetter,
  } = useAdminNotifications();

  const [tab, setTab] = useState("notifications");
  const [page, setPage] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchUnreadNotificationCount();
    fetchNotifications({ page, limit: 10 });
  }, [fetchNotifications, fetchUnreadNotificationCount, page]);

  useEffect(() => {
    if (tab === "dead-letters") fetchDeadLetters();
  }, [fetchDeadLetters, tab]);

  const items = (deadLetters || []).map(mapDeadLetter);

  const handleReplay = async (id) => {
    await replayDeadLetter(id);
    fetchDeadLetters();
  };

  const handleOpenNotification = async (id) => {
    setDetailOpen(true);
    await fetchNotification(id);
  };

  const handleMarkRead = async (id) => {
    await markNotificationRead(id);
    fetchUnreadNotificationCount();
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    fetchNotifications({ page, limit: 10 });
  };

  return (
    <div className="min-h-full bg-slate-50">
      <AdminNavBar
        title="Notifications"
        subTitle="Inbox & dead-letter queue"
        context="Read admin notifications and replay events that failed processing."
      />
      <main className="md:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="text-[26px] font-bold text-[#191c1e]">Notifications</h3>
              <p className="text-sm text-[#434655]">
                {unreadCount || 0} unread notification{unreadCount === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTab("notifications")}
                className={`rounded-lg px-4 py-2 text-xs font-semibold ${
                  tab === "notifications"
                    ? "bg-[#16213A] text-white"
                    : "border border-[#c3c6d7] bg-white text-[#434655]"
                }`}
              >
                Notifications
              </button>
              <button
                onClick={() => setTab("dead-letters")}
                className={`rounded-lg px-4 py-2 text-xs font-semibold ${
                  tab === "dead-letters"
                    ? "bg-[#16213A] text-white"
                    : "border border-[#c3c6d7] bg-white text-[#434655]"
                }`}
              >
                Dead Letters
              </button>
            </div>
          </div>

          {tab === "notifications" && (
            <div className="bg-white rounded-xl border border-[#c3c6d7] shadow-sm overflow-hidden">
              <div className="p-4 border-b border-[#c3c6d7] flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-[#434655]">
                  <Bell className="w-4 h-4" />
                  <span className="text-sm font-semibold">
                    {notificationsTotal || notifications.length} notifications
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleMarkAllRead}
                    disabled={notificationMutationLoading || (unreadCount || 0) === 0}
                    className="flex items-center gap-2 rounded-lg border border-[#c3c6d7] px-4 py-2 text-xs font-semibold transition-colors hover:bg-[#f2f4f6] disabled:opacity-50"
                  >
                    <CheckCheck className="w-4 h-4" /> Mark all read
                  </button>
                  <button
                    onClick={() => fetchNotifications({ page, limit: 10 })}
                    className="flex items-center gap-2 rounded-lg border border-[#c3c6d7] px-4 py-2 text-xs font-semibold transition-colors hover:bg-[#f2f4f6]"
                  >
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </button>
                </div>
              </div>

              {notificationsLoading && (
                <div className="px-4 py-3 text-sm text-[#434655]">Loading notifications…</div>
              )}
              {notificationsError && !notificationsLoading && (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-[#ba1a1a]">
                  <AlertTriangle className="w-4 h-4" /> {notificationsError}
                </div>
              )}
              {!notificationsLoading && notifications.length === 0 && !notificationsError && (
                <div className="px-4 py-10 text-center text-sm text-[#737686]">
                  You have no notifications.
                </div>
              )}

              {notifications.length > 0 && (
                <div className="divide-y divide-[#c3c6d7]/30">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`flex items-start justify-between gap-4 px-6 py-4 ${
                        notification.read ? "" : "bg-[#004ac6]/5"
                      }`}
                    >
                      <div>
                        <button
                          type="button"
                          onClick={() => handleOpenNotification(notification.id)}
                          className="flex items-center gap-2 text-left"
                        >
                          <span className="text-sm font-semibold text-[#191c1e]">
                            {notification.title}
                          </span>
                          {!notification.read && (
                            <span className="rounded-full bg-[#004ac6] px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                              New
                            </span>
                          )}
                        </button>
                        {notification.body && (
                          <p className="mt-1 text-sm text-[#434655]">{notification.body}</p>
                        )}
                        <p className="mt-1 text-xs text-[#737686]">
                          {notification.type} • {formatDate(notification.createdAt)}
                        </p>
                      </div>
                      {!notification.read && (
                        <button
                          onClick={() => handleMarkRead(notification.id)}
                          disabled={notificationMutationLoading}
                          className="shrink-0 rounded-lg border border-[#004ac6]/20 px-3 py-1.5 text-xs font-semibold text-[#004ac6] hover:bg-[#004ac6]/5 disabled:opacity-50"
                        >
                          Mark read
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {notificationsPages > 1 && (
                <div className="border-t border-[#c3c6d7] p-4">
                  <StaffPagination
                    page={page}
                    lastPage={notificationsPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </div>
          )}

          {tab === "dead-letters" && (
            <div className="bg-white rounded-xl border border-[#c3c6d7] shadow-sm overflow-hidden">
              <div className="p-4 border-b border-[#c3c6d7] flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-[#434655]">
                  <Bell className="w-4 h-4" />
                  <span className="text-sm font-semibold">
                    {deadLettersTotal || items.length} dead-letter event
                    {(deadLettersTotal || items.length) === 1 ? "" : "s"}
                  </span>
                </div>
                <button
                  onClick={() => fetchDeadLetters()}
                  className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg border border-[#c3c6d7] hover:bg-[#f2f4f6] transition-colors"
                >
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
              </div>

              {deadLettersLoading && (
                <div className="px-4 py-3 text-sm text-[#434655]">Loading notifications…</div>
              )}
              {deadLettersError && !deadLettersLoading && (
                <div className="px-4 py-3 text-sm text-[#ba1a1a] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {deadLettersError}
                </div>
              )}
              {!deadLettersLoading && items.length === 0 && !deadLettersError && (
                <div className="px-4 py-10 text-center text-sm text-[#737686]">
                  No dead-letter events. All notifications are healthy.
                </div>
              )}

              {items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-[#f2f4f6] text-[12px] tracking-wider text-[#434655] uppercase">
                        <th className="px-6 py-4 font-semibold">Event</th>
                        <th className="px-6 py-4 font-semibold">Target</th>
                        <th className="px-6 py-4 font-semibold text-right">Attempts</th>
                        <th className="px-6 py-4 font-semibold">Last Error</th>
                        <th className="px-6 py-4 font-semibold">Created</th>
                        <th className="px-6 py-4 font-semibold text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm text-[#191c1e]">
                      {items.map((row, idx) => (
                        <tr
                          key={row.id}
                          className={`hover:bg-[#004ac6]/5 transition-colors ${
                            idx === 0 ? "" : "border-t border-[#c3c6d7]/30"
                          }`}
                        >
                          <td className="px-6 py-4 font-medium">{row.event}</td>
                          <td className="px-6 py-4 text-[#434655]">{row.target}</td>
                          <td className="px-6 py-4 text-right">{row.attempts}</td>
                          <td
                            className="px-6 py-4 text-[#ba1a1a] max-w-[320px] truncate"
                            title={row.error}
                          >
                            {row.error}
                          </td>
                          <td className="px-6 py-4 text-[#434655]">{formatDate(row.createdAt)}</td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => handleReplay(row.id)}
                              disabled={notificationMutationLoading}
                              className="inline-flex items-center gap-1.5 text-[#004ac6] text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#004ac6]/20 hover:bg-[#004ac6]/5 disabled:opacity-50 transition-colors"
                            >
                              <RefreshCw className="w-3.5 h-3.5" /> Replay
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {detailOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-lg bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold">Notification</h3>
                <button
                  type="button"
                  onClick={() => setDetailOpen(false)}
                  className="text-slate-400 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>
              {notificationDetailLoading ? (
                <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
              ) : notificationDetailError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  {notificationDetailError}
                </div>
              ) : notificationDetail ? (
                <div className="space-y-3 text-sm">
                  <h4 className="text-base font-semibold">{notificationDetail.title}</h4>
                  {notificationDetail.body && (
                    <p className="text-slate-600">{notificationDetail.body}</p>
                  )}
                  <p className="text-xs text-slate-500">
                    {notificationDetail.type} • {formatDate(notificationDetail.createdAt)}
                  </p>
                  {notificationDetail.data && (
                    <pre className="max-h-48 overflow-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                      {JSON.stringify(notificationDetail.data, null, 2)}
                    </pre>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Notifications;
